/* eslint-disable no-unused-vars, no-new-func */

import { mapMutations, mapState } from 'vuex';
import axios from 'axios';
import gsap from 'gsap';
import jsonpath from 'jsonpath';
import { paramCase } from 'change-case';

import { triggerNames } from '@/data/events/triggers';
import { actionNames } from '@/data/events/actions';
import { conditionTargetNames } from '@/data/events/condition-targets';
import conditionOperators from '@/data/events/condition-operators';
import { FIRE_FLEX_EVENT } from '@/store/types/mutation-types';
import { FlexEventGroup } from '@/data/data-models';
import { tweenVarProps, tweenVarPropMap } from '@/data/animations/tween-vars';
import { getTransformValues } from '@/utils/css';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { getCookie, setCookie } from '@/utils/cookie';

const EVENT_ATTR_NAME = 'flex-event-group';
const FORM_ATTR_NAME = 'form-data';
const publicMode = process.env.VUE_APP_ENV === 'public';

class EventQueue {
    constructor() {
        return {
            click: new Map(),
            flexEvent: new Map(),
            load: new Map(),
            collectionLoad: new Map(),
            mouseenter: new Map(),
            mouseleave: new Map(),
            view: {}, // each view property represents a threshold, and contains a Map
            submit: new Map(),
            change: new Map(),
        };
    }
}

export default {

    mixins: [listenOnRootMixin],

    data() {
        return {
            clickNumber: 1,
            eventQueue: new EventQueue(),
            fieldComponents: [],
            observers: {},
        };
    },

    computed: {
        ...mapState({
            firedFlexEvent: state => state.firedFlexEvent,
            firedFlexEventCollectionData: state => state.firedFlexEventCollectionData,
            previewMode: state => state.previewMode,
            settings: state => state.settings,
            selectedElements: state => state.page.selectedElements,
        }),

        flexEventGroup() {
            return this.$parent.getElementData(EVENT_ATTR_NAME) || new FlexEventGroup();
        },

        flexEventGroupItems() {
            return Object.values(this.flexEventGroup).flat();
        },

        isEditingMode() {
            return !publicMode && !this.previewMode;
        },

        reCaptchaApiKey() {
            return this.settings.google.recaptchaKey || '6LdC3cgUAAAAAF6k6989YAtgr-OLsaOutK14eiRh';
        },
    },

    watch: {
        firedFlexEvent() {
            this.onFlexEvent();
        },

        flexEventGroup() {
            this.reset();
        },

        previewMode(isPreviewMode) {
            if (!isPreviewMode) this.reset();
        },
    },

    created() {
        this.listenOnRoot('flex::collection::load', this.onCollectionLoad);
    },

    async mounted() {
        this.setFlexEvents();
        this.onLoad();

        await this.$nextTick();
        this.fieldComponents = this.getFieldComponents();
    },

    methods: {
        ...mapMutations({
            fireFlexEvent: FIRE_FLEX_EVENT,
        }),

        getFieldComponents() {
            const elementTypes = ['a-checkbox', 'a-radio-button', 'a-input', 'a-select', 'a-textarea'];
            const components = [];

            function walk(parent) {
                if (elementTypes.includes(parent.elementType)) {
                    components.push(parent);
                }

                parent.$children.forEach(child => walk(child));
            }

            walk(this.$parent);

            return components;
        },

        submitForm(elem) {
            let { action, method } = this.$parent.getElementData(FORM_ATTR_NAME);

            if (!method) {
                console.warn('The form is missing a form method (GET, POST, ETC) value.');
                return;
            }

            method = method.toLowerCase();

            let formData = this.serializeFormData(elem, method);

            this.$root.$emit('flex::form::submit');

            if (method === 'query') {
                const cleanUrl = new URL(window.location.href.split('?')[0]);
                const urlParams = new URLSearchParams(cleanUrl.search);
                Object.entries(formData)
                    .forEach((entry) => {
                        const [key, value] = entry;
                        urlParams.set(key, value);
                    });
                window.history.replaceState({}, '', `${ window.location.pathname }?${ urlParams }`);
                this.emitOnRoot('flex::collection::query-refresh');
                this.onSubmitSuccess();
                return;
            }

            if (!action) {
                console.warn('The form is missing an action (endpoint) value.');
                return;
            }

            // handle relative links
            if (action.startsWith('/')) {
                if (process.env.VUE_APP_ENV === 'public') {
                    action = window.location.hostname + action;
                } else {
                    action = process.env.VUE_APP_CM_URL + action;
                }
            }

            // handle protocol
            action = action.startsWith('http') ? action : `${ window.location.protocol }//${ action }`;

            if (method === 'get') {
                const queryString = Object.entries(formData)
                    .map((entry) => {
                        const [key, value] = entry;

                        return `${ key }=${ value }`;
                    }).join('&');

                this.setCookies();

                action += `?${ queryString }`;
                formData = null;
                window.location.href = action;

                return;
            }

            axios[method](action, formData, { withCredentials: true })
                .then(this.onSubmitSuccess)
                .catch(this.onSubmitError);
        },

        onSubmit(e) {
            e.preventDefault();
            const context = this;
            const recaptcha = e.target.closest('form')
                .querySelectorAll('input[name="g-recaptcha-response"]');

            // if there is recaptcha, fire the execute method and then submit form
            if (window.grecaptcha && recaptcha.length > 0) {
                window.grecaptcha.execute(`${ this.reCaptchaApiKey }`, { action: 'validate_captcha' })
                    .then(function (token) {
                        recaptcha[0].value = token;
                        context.submitForm(e.target);
                    });
            // otherwise just submit form
            } else {
                this.submitForm(e.target);
            }
        },

        // returns a JS object where each key/value pair represents the form input name/value
        serializeFormData(form, method) {
            const formObject = [...new FormData(form)].reduce((formObj, formEntry) => {
                const [key, value] = formEntry;
                if (formObj[key] && value) {
                    formObj[key] = `${ formObj[key] }|${ value }`;
                } else if (!formObj[key]) {
                    formObj[key] = value;
                }
                return formObj;
            }, {});

            if (method === 'post') {
            // manually append false checkbox values for POST API ENDPOINT
                [...form.elements].forEach((el) => {
                    if (el.type === 'checkbox' && !el.checked && !formObject[el.name]) {
                        formObject[el.name] = 'false';
                    }
                });
            }

            return formObject;
        },

        setCookies() {
            this.fieldComponents.forEach((component) => {
                const { inputName, inputValueNew, getElementData } = component;
                const data = getElementData('form-cookie');

                if (!data || !inputName || !inputValueNew) return;

                const days = data.expiration;
                const numSeconds = 60 * 60 * 24 * days; // seconds x minutes x hours x days

                if (inputValueNew) {
                    setCookie(inputName, inputValueNew, numSeconds);
                }
            });
        },

        async onClick() {
            for (const eqItem of this.eventQueue.click) {
                const [, item] = eqItem;

                switch (true) {
                    // odd numbered clicks
                    case item.data.clickNumber === 1:
                        if (this.clickNumber % 2 !== 0) {
                            await item.func();
                        }

                        break;

                    // even numbered clicks
                    case item.data.clickNumber === 2:
                        if (this.clickNumber % 2 === 0) {
                            await item.func();
                        }

                        break;

                    // any click
                    default:
                        await item.func();
                }
            }

            this.clickNumber++;
        },

        async onFlexEvent() {
            for (const eqItem of this.eventQueue.flexEvent) {
                const [id, callback] = eqItem;

                await callback.func(this.firedFlexEvent.name);
            }
        },

        async onLoad() {
            for (const eqItem of this.eventQueue.load) {
                const [id, callback] = eqItem;

                await callback.func();
            }
        },

        async onCollectionLoad(collection) {
            for (const eqItem of this.eventQueue.collectionLoad) {
                const [id, callback] = eqItem;

                if (callback.data.collectionId !== collection.collectionId) return;

                await callback.func(collection);
            }
        },

        async onMouseenter() {
            for (const eqItem of this.eventQueue.mouseenter) {
                const [id, callback] = eqItem;

                await callback.func();
            }
        },

        async onMouseleave() {
            for (const eqItem of this.eventQueue.mouseleave) {
                const [id, callback] = eqItem;

                await callback.func();
            }
        },

        async onView(threshold) {
            for (const eqItem of this.eventQueue.view[threshold]) {
                const [id, callback] = eqItem;

                await callback.func();
            }
        },

        async onSubmitSuccess(resp) {
            this.setCookies();
            this.$root.$emit('flex::form::response');

            for (const eqItem of this.eventQueue.submit) {
                const [id, callback] = eqItem;

                if (callback.data.result === 'success') {
                    await callback.func({ respData: resp.data });
                }
            }
        },

        async onSubmitError() {
            this.$root.$emit('flex::form::response');

            for (const eqItem of this.eventQueue.submit) {
                const [id, callback] = eqItem;

                if (callback.data.result === 'error') {
                    await callback.func();
                }
            }
        },

        async onChange() {
            for (const eqItem of this.eventQueue.change) {
                const [id, callback] = eqItem;

                await callback.func();
            }
        },

        setEvent({ id, type, func, data }) {
            const eqItem = { func, data };

            // "view" triggers are stored as nested props, one prop for each intersection observer threshold
            // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
            if (type === triggerNames.VIEW) {
                this.setObserver(data);
                this.eventQueue[type][data.threshold] = this.eventQueue[type][data.threshold] || new Map();
                this.eventQueue[type][data.threshold].set(id, eqItem);
            } else {
                this.eventQueue[type].set(id, eqItem);
            }
        },

        setFlexEvents() {
            // load all of the flex event handlers
            this.flexEventGroupItems.forEach((flexEvent) => {
                const eventHandler = this.getFlexEventHandler(flexEvent);

                this.setEvent({
                    id: flexEvent.id,
                    type: flexEvent.trigger,
                    func: eventHandler,
                    data: flexEvent.data,
                });
            });
        },

        removeFlexEvents() {
            this.eventQueue = new EventQueue();
        },

        getFlexEventHandler(flexEvent) {
            let eventHandler = () => {};

            // fix for existing events created before the "conditions" prop was added
            flexEvent.conditions = flexEvent.conditions || [[]];

            switch (flexEvent.action) {
                case actionNames.SHOW:
                    eventHandler = (eventContext) => {
                        const target = this.getFlexEventTarget(flexEvent.data);

                        if (!target) {
                            console.warn(`Unable to find an element that matches the ID "${ flexEvent.data.targetId }"`);
                            return;
                        }

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext, target)) return;

                        if (target.className.includes('flex')) {
                            target.style.display = 'flex';
                        } else {
                            target.style.display = 'block';
                        }
                    };

                    break;

                case actionNames.HIDE:
                    eventHandler = (eventContext) => {
                        const target = this.getFlexEventTarget(flexEvent.data);

                        if (!target) {
                            console.warn(`Unable to find an element that matches the ID "${ flexEvent.data.targetId }"`);
                            return;
                        }

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext, target)) return;

                        target.style.display = 'none';
                    };

                    break;

                case actionNames.TOGGLE:
                    eventHandler = (eventContext) => {
                        const target = this.getFlexEventTarget(flexEvent.data);

                        if (!target) {
                            console.warn(`Unable to find an element that matches the ID "${ flexEvent.data.targetId }"`);
                            return;
                        }

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext, target)) return;

                        target.style.display = target.style.display === 'block' ? 'none' : 'block';
                    };

                    break;

                case actionNames.TOGGLE_LAYERED_CONTAINER:
                    eventHandler = (eventContext) => {
                        const { targetId, layerNum } = flexEvent.data;

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext)) return;

                        this.$root.$emit('flex::layered-container::switch', {
                            targetId,
                            layerNum: Number(layerNum),
                        });
                    };

                    break;

                case actionNames.URL_REDIRECT:
                    eventHandler = (eventContext) => {
                        const { url, target } = flexEvent.data;

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext, target)) return;

                        if (target === '_blank') {
                            window.open(url);
                        } else {
                            window.location.href = url;
                        }
                    };

                    break;

                case actionNames.SCROLL_INTO_FOCUS:
                    eventHandler = (eventContext) => {
                        const target = this.getFlexEventTarget(flexEvent.data);

                        if (!target) {
                            console.warn(`Unable to find an element that matches the ID "${ flexEvent.data.targetId }"`);
                            return;
                        }

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext, target)) return;

                        const smoothScrollSupport = 'scrollBehavior' in document.documentElement.style;
                        const yCoord = target.getBoundingClientRect().top + window.scrollY;

                        if (smoothScrollSupport) {
                            window.scrollTo({
                                top: yCoord,
                                behavior: 'smooth',
                            });
                        } else {
                            window.scrollTo(0, yCoord);
                        }
                    };

                    break;

                case actionNames.SCROLL_TO_TOP:
                    eventHandler = (eventContext) => {
                        const smoothScrollSupport = 'scrollBehavior' in document.documentElement.style;

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext)) return;

                        if (smoothScrollSupport) {
                            window.scrollTo({
                                top: 0,
                                behavior: 'smooth',
                            });
                        } else {
                            window.scrollTo(0, 0);
                        }
                    };

                    break;

                case actionNames.RUN_ANIMATION:
                    eventHandler = (eventContext) => {
                        const { animationId } = flexEvent.data;
                        const animation = this.settings.animations.find(a => a.id === animationId);
                        const target = this.getFlexEventTarget(flexEvent.data);

                        if (!animation) {
                            console.warn(`Unable to locate an animation matching the ID "${ animationId }"`);
                            return;
                        }

                        if (!target) {
                            console.warn(`Unable to find an element that matches the ID "${ flexEvent.data.targetId }"`);
                            return;
                        }

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext, target)) return;

                        const { tweens, repeat, repeatDelay, repeatAlternate } = animation;
                        const timeline = gsap.timeline({ paused: true, repeat, repeatDelay });

                        timeline.yoyo(repeatAlternate);

                        tweens.forEach((tween) => {
                            const startingVars = {};
                            const endingVars = {};

                            tween.vars.forEach((tweenVar) => {
                                const prop = tweenVarPropMap[tweenVar.prop];

                                startingVars[prop] = this.getStartingVarValue(tweenVar, target);
                                endingVars[prop] = this.getEndingVarValue(tweenVar, target);
                            });

                            timeline.fromTo(target, startingVars, {
                                ...endingVars,
                                duration: tween.duration,
                                delay: tween.delay,
                                ease: tween.ease,
                            });
                        });

                        timeline.play();
                    };

                    break;

                case actionNames.RUN_JAVASCRIPT:
                    eventHandler = (eventContext) => {
                        const target = this.getFlexEventTarget(flexEvent.data);

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext, target)) return;

                        try {
                            const code = flexEvent.data.code.replace(/\$data/g, 'arguments[0]');
                            Function(code).call(target, eventContext?.respData);
                        } catch (e) {
                            console.warn('Unable to run the custom code snippet:', e);
                        }
                    };

                    break;

                case actionNames.VALIDATE_FORM:
                    eventHandler = (eventContext) => {
                        const target = this.getFlexEventTarget(flexEvent.data);

                        if (!target) {
                            console.warn(`Unable to find a form element that matches the ID "${ flexEvent.data.targetId }"`);
                            return;
                        }

                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext, target)) return;

                        target.reportValidity();
                    };

                    break;

                case actionNames.FIRE_EVENT:
                    eventHandler = (eventContext) => {
                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext)) return;

                        this.fireFlexEvent(flexEvent.data.eventName);
                    };

                    break;

                case actionNames.PAUSE:
                    eventHandler = (eventContext) => {
                        // temporarily hard-coded index, until we fully support "and" + "or" conditions
                        // see FlexEvent in data-models.js for more info
                        if (!this.hasValidConditions(flexEvent.conditions[0], eventContext)) return;

                        return new Promise((resolve) => {
                            window.setTimeout(() => {
                                resolve();
                            }, flexEvent.data.seconds * 1000);
                        });
                    };

                    break;

                default:
                    console.warn('Invalid Flex action:', flexEvent.action);
            }

            // wrap the event handler for flex event actions
            // so we can check if the event matches
            if (flexEvent.trigger === triggerNames.FLEX_EVENT) {
                return (flexEventName) => {
                    if (flexEventName !== flexEvent.data.eventName) return;

                    return eventHandler();
                };
            }

            return eventHandler;
        },

        getStartingVarValue(tweenVar, target) {
            const transformObj = getTransformValues(target.style.transform);
            const value = window.getComputedStyle(target).getPropertyValue(paramCase(tweenVar.prop));

            if (!transformObj) return value;

            // for UP/DOWN/LEFT/RIGHT animations, we can't simply pass in the tweenVar prop to window.getComputedStyle
            // ... since the prop will be either "x" or "y", which doesn't exist in CSS
            switch (tweenVar.prop) {
                case tweenVarProps.UP:
                case tweenVarProps.DOWN:
                    return `${ transformObj.y }px`;

                case tweenVarProps.LEFT:
                case tweenVarProps.RIGHT:
                    return `${ transformObj.x }px`;

                default:
                    return value;
            }
        },

        getEndingVarValue(tweenVar, target) {
            const transformObj = getTransformValues(target.style.transform);

            let value = tweenVar.value;

            if (!transformObj) return `${ value }${ tweenVar.unit }`;

            // special cases needed for the "move" animations
            // we need to take the existing transform values into account before calculating the end position
            switch (tweenVar.prop) {
                case tweenVarProps.UP:
                case tweenVarProps.DOWN:
                    value = Number(value) + transformObj.y;

                    return `${ value }${ tweenVar.unit }`;

                case tweenVarProps.LEFT:
                case tweenVarProps.RIGHT:
                    value = Number(value) + transformObj.x;

                    return `${ value }${ tweenVar.unit }`;

                default:
                    return `${ value }${ tweenVar.unit }`;
            }
        },

        getFlexEventTarget(flexEventData) {
            const { targetType, targetId } = flexEventData;

            switch (true) {
                case !targetType && !targetId:
                    return null;

                case targetType === 'self':
                    return this.$parent.$el;

                default:
                    return document.getElementById(targetId);
            }
        },

        setObserver({ threshold }) {
            if (this.observers[threshold]) {
                this.observers[threshold].unobserve(this.$el);
            }

            const wrappedCallback = (entries) => {
                // we only want this trigger to fire once
                if (entries[0].isIntersecting && !this.isEditingMode) {
                    this.onView(threshold);
                    this.observers[threshold].unobserve(this.$el);
                }
            };

            this.observers[threshold] = new IntersectionObserver(wrappedCallback, { threshold });
            this.observers[threshold].observe(this.$el);
        },

        hasValidConditions(conditions, eventContext, target) {
            return conditions.every((condition) => {
                const { targetProperty, targetValue } = condition;

                switch (condition.target) {
                    case conditionTargetNames.COOKIE: {
                        const cookieValue = getCookie(targetProperty);

                        return this.checkCondition(condition, cookieValue, targetValue);
                    }

                    case conditionTargetNames.FORM:
                    case conditionTargetNames.FORM_FIELD:
                        return this.checkCondition(condition, target);

                    case conditionTargetNames.FORM_RESPONSE: {
                        if (!eventContext.respData) return false;

                        const dataPoints = jsonpath.query(eventContext.respData, targetProperty); // targetProperty = the JSONPath entered by the user

                        return this.checkCondition(condition, dataPoints[0], targetValue);
                    }

                    case conditionTargetNames.COLLECTION: {
                        return this.checkCondition(condition, eventContext.data);
                    }

                    default:
                        return false;
                }
            });
        },

        checkCondition(condition, valueA, valueB) {
            const { computation } = conditionOperators.get(condition.operator);

            return computation(valueA, valueB);
        },

        reset() {
            this.removeFlexEvents();
            this.setFlexEvents();
        },
    },
};
